import axios from "axios";
import { datadogRum } from "@datadog/browser-rum";
import partnerCodeHandler, { splashPageHandler } from "./interceptor";
import CONSTANTS, {
  API_COMBO,
  JACADA_DEV_URL,
  JACADA_PROD_URL,
  PEP
} from "../app-consts/appConstants";
import getUserTypeFromUrl from "../utils/getUserTypeFromUrl";
import { ErrorSource } from "@datadog/browser-core";
import { sanitizeVehiclePayload, getLocationCodeFromUrl } from "../utils";
import { generatePathWithSearchParams } from "../utilities/RedirectUtils";
import { getPersistor } from "../getStore";
import { storage } from "../storage";
import { StorageKey } from "../storage/storageRules";


export const local_wle_proxy_url = process.env.REACT_APP_LOCAL
  ? "http://localhost:8080"
  : "";
const endpoint_url = process.env.REACT_APP_LOCAL ? "http://localhost:8883" : "";
export const drw_endpoint_url = process.env.REACT_APP_LOCAL
  ? "http://localhost:8888"
  : "";
const userType = getUserTypeFromUrl();
const make_model = `/wle/${userType}/vehicle/year`;
const create_session = "/" + userType + "/api/createSession";
const provider_info = "/" + userType + "/api/getProviderInfo";

axios.interceptors.request.use((request) => partnerCodeHandler(request));

axios.interceptors.response.use(splashPageHandler, (error) => {
  return Promise.reject(error);
});

export const getIncidentReportQuestions = async (reason) => {
  try {
    return axios
      .post(`${local_wle_proxy_url}/api/omni/rescue/v1/incident-questions`, {
        partnerCode: "CRA",
        reason,
        requestedBy: "omniassist",
      })
      .then((res) => {
        if (res.data.errorCode || res.data.errorMessage) {
          datadogRum.addError(
            "Error returned from omni api incident-questions endpoint"
          );
          throw new Error({
            errorCode: res.data.errorCode,
            errorMessage: res.data.errorMessage,
          });
        } else if (res.data.body.length < 1) {
          datadogRum.addError(
            "No questions returned from omni api incident-questions endpoint"
          );
          throw new Error("No questions returned from Omni api");
        }

        return res.data.body;
      });
  } catch (err) {
    datadogRum.addError(
      "Call to fetch incident report form questions resulted in error: ",
      err
    );
  }
};

export async function callToGetTimeZone(latitude, longitude, time) {
  return axios
    .get(
      `${endpoint_url}/wle/timezone?location=${latitude},${longitude}&timestamp=${time}`
    )
    .then((response) => {
      return response;
    })
    .catch((error) => {
      datadogRum.addError(
        `Call to Google Timezone API resulted in an error: `,
        { latitude, longitude, timestamp: time, error }
      );
    });
}

export async function getPredictiveETA({
  currentLocation,
  isTow,
  appId,
  sessionId,
  partnerCode,
  callId,
}) {
  const addressDetails = {
    latitude: currentLocation.latitude,
    longitude: currentLocation.longitude,
    zipCode: currentLocation.zipCode,
    stateCode: currentLocation.state,
    towInd: isTow ? "Y" : "N",
    sourceName: appId,
    sessionId: sessionId,
    partnerCode: partnerCode,
    callId,
  };
  return await axios
    .post(`${endpoint_url}/wle/getPredictiveETA`, addressDetails)
    .then((response) => {
      datadogRum.addAction("Predictive ETA endpoint hit: ", addressDetails);
      return response?.data;
    })
    .catch((error) => {
      addressDetails.error = error;
      datadogRum.addError(
        "Call to Predictive ETA API resulted in an error: ",
        addressDetails
      );
    });
}

export function getMakeModelApi(year) {
  return axios
    .get(`${endpoint_url}${make_model}/${year}`)
    .then((response) => ({ response }))
    .catch((error) => ({ error: error.response }));
}

export function submitServiceRequest(
  payload,
  partnerCode,
  serviceType,
  experience,
  hasPepSession = false,
  omniStreamlined = ""
) {
  const sessionId = payload.sessionId;
  const appId = payload.serviceRequest.appId;
  let isPpu = payload.serviceRequest.ppuFlag === "Y";
  if (
    (!experience?.ppuEnabled && !experience?.benefitsEnabled) ||
    hasPepSession
  ) {
    isPpu = true;
  }
  let url = "";
  payload.locationCode = getLocationCodeFromUrl()
  url = `${drw_endpoint_url}/api/omniapi/${appId}/partner/${partnerCode}/rescue?serviceType=${serviceType}&final=Y`;
  return axios.post(url, payload, { headers: { isPpu: `${isPpu}` } });
}

export function createSessionRequest(createSessionRequest) {
  return axios.post(
    `${endpoint_url}${create_session}?disablementlatitude=${createSessionRequest.disablementlatitude}&disablementlongitude=${createSessionRequest.disablementlongitude}&appId=${createSessionRequest.appId}`,
    {},
    { headers: {}, handlerEnabled: true }
  );
}

export function getPartnerDetailsApi(route, displayCode) {
  return axios.get(`${endpoint_url}/wle/${route}/partner/${displayCode}`);
}

export function userBenefitsApi(sessionId) {
  const userType_getBenefits_path =
    "/" + getUserTypeFromUrl() + "/api/getBenefits";
  return axios
    .post(
      `${endpoint_url}${userType_getBenefits_path}?sessionId=${sessionId}`,
      {}
    )
    .then(
      (response) => {
        return response;
      },
      (error) => {
        return error.response;
      }
    );
}

export async function createMemberSessionOpenApi(
  route,
  {
    memberId,
    partnerCode,
    type,
    createSessionRequest,
    phoneLookup,
    appId,
    details,
    vin,
    mileage,
    flippedToPpu,
    arsToken,
    lastName,
    rentalAgreementNumber,
  }
) {
  let customerVin = "",
    customerMileage = "",
    latitude = "",
    longitude = "",
    detail = "";

  if (createSessionRequest) {
    customerVin = createSessionRequest.vin;
    customerMileage = createSessionRequest.mileage;
    latitude = createSessionRequest.latitude
      ? createSessionRequest.latitude
      : "";
    longitude = createSessionRequest.longitude
      ? createSessionRequest.longitude
      : "";
  }
  if (details) {
    const { firstName, lastName, zip, phone, companyName, stateCode } = details;
    detail = `${firstName ? firstName : null},${lastName ? lastName : null},${
      zip ? zip : null
    },${phone ? phone : null},${companyName ? companyName : null},${
      stateCode ? stateCode : null
    }`;
  }
  const body = {
    memberIdentifier: memberId,
    vin: vin ? vin : customerVin,
    mileage: mileage ? mileage : customerMileage,
    latitude,
    longitude,
    details: detail,
    phoneLookup,
    flippedToPpu,
    arsToken,
    lastName,
    rentalAgreementNumber,
  };

  const url = `${drw_endpoint_url}/api/openapi/${route}/${appId}/partner/${partnerCode}/session?type=${type}`;
  let retry = 0;
  while (retry < 3) {
    try {
      let response = await axios.post(url, body);
      if (retry) {
        let message = `Create Session api call failed and recovered for memberId: ${memberId}, detail: ${detail}`;
        datadogRum.addError(new Error(message));
      }
      return response;
    } catch (err) {
      if (err.response?.status === 500 && retry < 2) {
        retry += 1;
      } else {
        datadogRum.addError(
          new Error('Open API session creation failed'), {body, err}
        )
        return err.response;
      }
    }
  }
}

export async function fetchCommonBenefitsForUser(
  sessionId,
  appId,
  partnerCode
) {
  const conversationId = localStorage.getItem("conversationId");
  let url = `${drw_endpoint_url}/api/openapi/${appId}/partner/${partnerCode}/session/${sessionId}/benefits`;
  let urlSearchParams = [];
  if (conversationId) {
    urlSearchParams.push(["conversationId", conversationId]);
  }
  if (urlSearchParams.length > 0) {
    url += `?${urlSearchParams
      .map(([key, value]) => `${key}=${value}`)
      .join("&")}`;
  }
  let retry = 0;
  let errors = [];
  while (retry < 3) {
    try {
      let response = await axios.get(url);
      if (response?.status === 204) {
        datadogRum.addAction(`Open API benefits thrown 204 status for appId: ${appId}, partnerCode: ${partnerCode}, sessionId: ${sessionId}`);
      }
      if (retry) {
        let message = `Common Benefits api call failed and recovered for appId: ${appId}, partnerCode: ${partnerCode}, sessionId: ${sessionId}`;
        datadogRum.addAction(message);
      }
      return response;
    } catch (err) {
      if (err.response?.status === 500 && retry < 2) {
        errors[retry] = err;
        retry += 1;
      } else {
        datadogRum.addError(
          new Error(`Open API benefits api thrown error for ${appId} ${partnerCode}`), {err}
        );
        return err.response;
      }
      if (retry >= 3) {
        datadogRum.addError(
          new Error(`Open API benefits thrown 500 error ${retry} times ${appId} ${partnerCode}`), {errors}
        );
      }
    }
  }
}

export function recoverFromInvalidOrExpiredSession(
  history,
  dispatch,
  removeQueryParams
) {
  const url1 = clearLocalStorageAndGenerateUrl(removeQueryParams);
  dispatch();
  setTimeout(() => {
    datadogRum.addError(
      new Error("Recovering from invalid or expired session")
    );
    history.push(url1);
  }, 1000);
}

export function clearLocalStorageAndGenerateUrl(removeQueryParams) {
  let tollFreeNumber = localStorage.getItem("tollFreeNumber");
  getPersistor().purge();
  localStorage.clear();
  localStorage.setItem("tollFreeNumber", tollFreeNumber);

  let route = window.location.pathname.split("/")[1];
  let partner = window.location.pathname.split("/")[2];
  let url1;
  if (route === "benefits") {
    url1 = generatePathWithSearchParams(`/${route}/${partner}/memberlookup`);
  } else {
    url1 = generatePathWithSearchParams(`/${route}/${partner}/lookup`);
  }

  if (removeQueryParams) {
    const index = url1.indexOf("?");
    url1 = index !== -1 ? url1.substring(0, index) : url1;
  }

  sessionStorage.setItem("invalid_session", "true");

  return url1;
}

export function getPartnerSpecificTowToLocations(
  location,
  appId,
  partnercode,
  countrycode
) {
  const { lat, lng } = location;
  const sessionId = localStorage.getItem("benefitSession");
  return axios
    .post(
      `${endpoint_url}/openapi/${appId}/partner/${partnercode}/session/${sessionId}/towlocations?lat=${lat}&lng=${lng}&countrycode=${countrycode}`,
      {}
    )
    .then(
      (response) => {
        return {
          destinations: response.data.towTo,
          customDestinationList: response.data.destinationsList,
        };
      },
      (error) => {
        datadogRum.addError(
          new Error(`getPartnerSpecificTowToLocations error: ${error}`)
        );
        return { error: error.response };
      }
    );
}

export function sendJacadaRequest(data) {
  if (!window.location.href.toLowerCase().includes("omniassist")) {
    return;
  }

  const params = new URLSearchParams(window.location.search);
  data.externalSystemId = params.get("conversationId");

  if (!data.externalSystemId) {
    datadogRum.addError(
      new Error(
        "Could not send Jacada request becasue externalSystemId was undefined"
      ),
      undefined,
      ErrorSource.CUSTOM
    );
    return;
  }
  if (
    window.location.href.indexOf("D24") > -1 ||
    window.location.href.indexOf("OmniAssist") === -1
  ) {
    return;
  }

  data.events?.forEach((event) => {
    if (event.value === undefined || event.value === null) event.value = "";
  });

  let baseUrl =
    window.location.href.indexOf("https://roadside.allstate.com") > -1
      ? JACADA_PROD_URL
      : JACADA_DEV_URL;
  const url = `${baseUrl}/${data.externalSystemId}/events`;
  let envHeader =
    window.location.href.indexOf("https://roadside.allstate.com") > -1
      ? `Prod`
      : `Dev`;
  let axiosJacada = axios;
  datadogRum.addAction("jacadaEvent", { url, data, envHeader });
  return axiosJacada
    .post(
      url,
      { data },
      { headers: { "x-api-key": API_COMBO, env: envHeader } }
    )
    .then((response) => {
      return response;
    });
}

export function getTokenFromCookie() {
  try {
    let cookieMatches = storage.get(StorageKey.UserInfo);;
    return cookieMatches ? cookieMatches?.token : "";
  } catch (e) {
    console.error("Error retrieving token: ", e);
    return "";
  }
}

export async function getVerifiedRescueTrackerStatus(partnerCode, usageId, callId) {
  try {
    const response = await axios.get(
      `${endpoint_url}/wle/tracker/partner/${partnerCode}/usageid/${usageId}/callid/${callId}/rescuestatus`
    );
    if (response?.data?.error) {
      datadogRum.addError("rescueStatusTrackerError-response", {
        partnerCode,
        usageId,
        callId,
        response,
      });
    } else {
      return response.data;
    }
  } catch (error) {
    datadogRum.addError(
      `Rescue Status View Error: ${
        error?.response?.data?.error?.errors?.filter(
          (_x, index) => index === 0
        )?.[0] ?? error?.message
      }`,
      { rescueStatusViewError: { partnerCode, usageId, error } }
    );
    // 400 = rescue verification failed (partnerCode/callId mismatch or invalid params); let callers handle it.
    if (error?.response?.status === 400) {
      throw error;
    }
  }
}

export function getShortenedUrl(partnerCode, usageId, url) {
  return axios
    .post(`${endpoint_url}/wle/urlshortener`, { url: url })
    .then((response) => response.data.shorturl)
    .catch((error) => {
      datadogRum.addError("urlShortenerError", { partnerCode, usageId, error });
    });
}

export async function searchRescue(
  partnerCode = "",
  appId = "",
  lookupType = "",
  searchBy = "",
  postFix = ""
) {
  const body = {
    lookupType,
    searchBy,
    postFix
  };

  const url = `${drw_endpoint_url}/api/omniapi/${appId}/partner/${partnerCode}/searchrescue`;

  try {
    let response = await axios.post(url, body);
    return response;
  } catch (err) {
    return err.response;
  }
}

export async function saveRescueDetails(
  serviceRequestPayload,
  partnerCode,
  serviceType,
  history,
  dispatch
) {
  let { sessionId } = serviceRequestPayload;

  if(partnerCode === PEP) {
    serviceRequestPayload.serviceRequest.customerType = localStorage.getItem("customerType");
  }
  if (!sessionId || sessionId.includes("-")) {
    recoverFromInvalidOrExpiredSession(history, dispatch);
    datadogRum.addAction(
      "SaveRescueDetails - session ID not available or not valid - User recovered"
    );
    return;
  }

  let url = `${drw_endpoint_url}/api/omniapi/${serviceRequestPayload.serviceRequest.appId}/partner/${partnerCode}/rescue`;
  if (serviceType) {
    url += `?serviceType=${serviceType}`;
  }
  if (serviceType && serviceType?.toLowerCase() !== "tow") {
    delete serviceRequestPayload.towReason;
  }

  serviceRequestPayload.locationCode = getLocationCodeFromUrl()
  try {
    if (serviceRequestPayload?.promoCode) {
      return await axios.post(url, serviceRequestPayload);
    } else {
      datadogRum.addAction("promoCode not available - User recovered", { sessionId, partnerCode, serviceType, serviceRequestPayload });
      recoverFromInvalidOrExpiredSession(history, dispatch);
      return;
    }
  } catch (err) {
    if (
      err.response &&
      err.response.status === 400 &&
      err.response.data &&
      typeof err.response.data.message === "string" &&
      err.response.data.message.includes("Session Expired due to older than") &&
      err.response.data.message.includes("creation date time.")
    ) {
      datadogRum.addError(
        new Error(
          `Omni Search API: Session Expired. appId: ${serviceRequestPayload.serviceRequest.appId}, partnerCode: ${partnerCode}.`
        ),
        {
          status: err.response.status,
          path: err.response.data.path,
        }
      );
      recoverFromInvalidOrExpiredSession(history, dispatch, true);
      return;
    }
    datadogRum.addError("[saveRescueDetails] error in saving rescue", err);
    return err.response;
  }
}

export function getOmniPartnerToLocations(
  location,
  appId,
  partnercode,
  countryCode,
  locationTypeId,
  promoCode,
  isPayPeruse,
  utcOffset,
  memberId,
  postFix,
  customerType
) {
  const locationCode = getLocationCodeFromUrl()
  const payload = {
    lat: location.lat,
    lng: location.lng,
    locationTypeId,
    countryCode,
    isPayPeruse,
    utcOffset,
    memberId,
    postFix,
    locationCode,
    customerType
  };

  return axios
    .post(
      `${drw_endpoint_url}/api/omniapi/${appId}/partner/${partnercode}/promocode/${promoCode}/towlocations`,
      payload
    )
    .then(
      (response) => {
        return {
          destinations: response.data.towTo,
          customDestinationList: response.data.destinationsList,
        };
      },
      (error) => {
        datadogRum.addError(
          new Error(`getOmniPartnerToLocations error: ${error}`)
        );
        return { error: error.response };
      }
    );
}

export async function searchOngoingRescue(
  partnerCode = "",
  appointmentNumber = ""
) {
  const body = { appointmentNumber };
  const url = `${drw_endpoint_url}/api/omniapi/partner/${partnerCode}/${appointmentNumber}/searchOngoing`;
  let retry = 0;
  while (retry < 3) {
    try {
      let response = await axios.post(url, body);
      if (retry) {
        datadogRum.addAction(`search Ongoing Rescue error retry ${retry} for ${partnerCode} and rescue ${appointmentNumber}`, { response: response});
      }
      return response?.status !== 204;
    } catch (err) {
      if (err && retry < 2) {
        retry += 1;
      } else {
        datadogRum.addError(`search Ongoing Rescue max error retries for ${partnerCode} and rescue ${appointmentNumber}`, { error: err});
        return true;
      }
    }
  }

}

export async function fetchPPUBenefits(options, appId, partnerCode, promoCode) {
  let url = `${drw_endpoint_url}/api/omniapi/${appId}/partner/${partnerCode}/promoCode/${promoCode}/retrievePPUBenefits`;
  const conversationId = localStorage.getItem("conversationId");
  let urlSearchParams = [];
  if (conversationId) {
    urlSearchParams.push(["conversationId", conversationId]);
  }
  if (urlSearchParams.length > 0) {
    url += `?${urlSearchParams
      .map(([key, value]) => `${key}=${value}`)
      .join("&")}`;
  }
  let firstName, lastName, zipCode, stateCode, callId, businessName, customerType;
  if (options.details) {
    firstName = options.details.firstName ? options.details.firstName : null;
    lastName = options.details.lastName ? options.details.lastName : null;
    zipCode = options.details.zip ? options.details.zip : null;
    stateCode = !!options?.details?.stateCode
      ? options?.details?.stateCode
      : null;
    businessName = options.details.businessName ? options.details.businessName : null;
  } else if (options.lastName) {
    lastName = options.lastName;
  }
  if (options.searchCallId) {
    callId = options.searchCallId;
  } else {
    callId = options.callId;
  }
  customerType = options.customerType;
  const body = {
    partnerMemberId: options?.partnerMemberId,
    vin: options?.vin,
    firstName,
    lastName,
    zipCode,
    stateCode,
    phoneNumber: options?.phoneNumber,
    tollFreeNumber: options?.tollFreeNumber,
    jobId: options?.jobId,
    lookupType: options?.lookupType,
    callId: callId,
    usageId: options?.usageId,
    guestFlow: options?.guestFlow,
    searchCallId: options?.searchCallId,
    appointmentNumber: options?.searchCallId,
    businessName,
    customerType,
    locationCode: getLocationCodeFromUrl()
  };
  if (
    options.lookupType === CONSTANTS.AUTO_PHONE_LOOKUP ||
    options.lookupType === CONSTANTS.PHONE_NUMBER
  ) {
    body.searchBy = options.phoneNumber;
  }
  let retry = 0;
  while (retry < 3) {
    try {
      let response = await axios.post(url, body);
      if (retry) {
        let message = `Omni Search API call failed and recovered for appId: ${appId}, partnerCode: ${partnerCode}`;
        datadogRum.addError(new Error(message));
      }
      return response;
    } catch (err) {
      if (err.response?.status === 500 && retry < 2) {
        retry += 1;
      } else {
        datadogRum.addError(
          new Error(`Fetch PPU- Omni Search API call failed with status ${err.response?.status}`), {body, err}
        );
        return err.response;
      }
    }
  }
}
